import { readAccountStorageItem, writeAccountStorageItem } from "../utils/accountStorage";

const CACHE_PREFIX = "scheduler.cache.";
const QUEUE_KEY = "scheduler.offline.queue.v1";
const LAST_SYNC_KEY = "scheduler.offline.lastsync";
const TIMELINE_PATCH_KEY = "scheduler.offline.timeline.patches.v1";

function safeParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.warn("Failed to parse offline payload", err);
    return null;
  }
}

function broadcastQueueChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  }
}

function isQuotaExceededError(err) {
  const name = String(err?.name || "");
  const message = String(err?.message || "").toLowerCase();
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    err?.code === 22 ||
    err?.code === 1014 ||
    message.includes("quota")
  );
}

function clearResponseCaches() {
  if (typeof localStorage === "undefined") return;
  const keys = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(CACHE_PREFIX)) {
      keys.push(key);
    }
  }
  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Cache eviction is best effort.
    }
  });
}

export function saveCache(key, data) {
  if (typeof localStorage === "undefined") return;
  const payload = { savedAt: Date.now(), data };
  const storageKey = `${CACHE_PREFIX}${key}`;
  const serialized = JSON.stringify(payload);
  try {
    localStorage.setItem(storageKey, serialized);
  } catch (err) {
    if (isQuotaExceededError(err)) {
      clearResponseCaches();
      try {
        localStorage.setItem(storageKey, serialized);
        return;
      } catch (retryErr) {
        console.warn("Unable to persist response cache after clearing old caches", retryErr);
        return;
      }
    }
    console.warn("Unable to persist response cache", err);
  }
}

export function readCache(key) {
  const parsed = readCacheEnvelope(key);
  return parsed ? parsed.data : null;
}

export function readCacheEnvelope(key) {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
  const parsed = safeParse(raw);
  if (!parsed || typeof parsed !== "object") return null;
  return parsed;
}

export function removeCache(key) {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(`${CACHE_PREFIX}${key}`);
}

function loadQueue() {
  return safeParse(readAccountStorageItem(QUEUE_KEY)) || [];
}

function saveQueue(queue) {
  writeAccountStorageItem(QUEUE_KEY, JSON.stringify(queue));
  broadcastQueueChanged();
}

export function enqueueOfflineAction(entry) {
  const queue = loadQueue();
  const action = {
    id:
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`),
    created_at: Date.now(),
    ...entry,
    meta: entry.meta || null,
  };
  queue.push(action);
  saveQueue(queue);
  return action;
}

export function getOfflineMeta() {
  if (typeof localStorage === "undefined") {
    return { count: 0, lastSyncedAt: null, actions: [] };
  }
  const queue = loadQueue();
  const last = Number(readAccountStorageItem(LAST_SYNC_KEY) || "0") || null;
  const actions = queue.map((entry) => ({
    id: entry.id,
    created_at: entry.created_at,
    meta: entry.meta || null,
    path: entry.path,
    method: entry.method,
  }));
  return { count: queue.length, lastSyncedAt: last, actions };
}

function loadTimelinePatches() {
  return safeParse(readAccountStorageItem(TIMELINE_PATCH_KEY)) || [];
}

function saveTimelinePatches(patches) {
  writeAccountStorageItem(TIMELINE_PATCH_KEY, JSON.stringify(patches));
}

export function recordTimelinePatch(patch) {
  if (!patch?.appointment_id) return;
  const patches = loadTimelinePatches();
  patches.push({
    actionId: patch.actionId,
    appointment_id: patch.appointment_id,
    changes: patch.changes || {},
    reason: patch.reason || "offline-change",
    created_at: Date.now(),
  });
  saveTimelinePatches(patches);
}

export function removeTimelinePatchesForActions(actionIds = []) {
  if (!actionIds?.length) return;
  const patches = loadTimelinePatches();
  const next = patches.filter((patch) => !actionIds.includes(patch.actionId));
  saveTimelinePatches(next);
}

export function clearTimelinePatches() {
  saveTimelinePatches([]);
}

export function applyTimelinePatches(timeline) {
  const patches = loadTimelinePatches();
  if (!timeline || !patches.length) return timeline;
  const patchMap = new Map();
  patches.forEach((patch) => {
    if (!patchMap.has(patch.appointment_id)) {
      patchMap.set(patch.appointment_id, []);
    }
    patchMap.get(patch.appointment_id).push(patch);
  });
  const cloned = {
    ...timeline,
    entries: (timeline.entries || []).map((entry) => ({ ...entry })),
  };
  cloned.entries = cloned.entries.map((entry) => {
    const patchList = patchMap.get(entry.appointment_id);
    if (!patchList?.length) return entry;
    const merged = { ...entry };
    patchList.forEach((patch) => {
      Object.assign(merged, patch.changes);
      merged.__pending = true;
      merged.__pending_reason = patch.reason;
    });
    return merged;
  });
  return cloned;
}

export async function flushOfflineQueue(dispatch) {
  const queue = loadQueue();
  if (!queue.length) {
    return { flushed: 0, remaining: 0 };
  }
  const remaining = [];
  let flushed = 0;
  const processedIds = [];
  for (let i = 0; i < queue.length; i += 1) {
    const entry = queue[i];
    try {
      await dispatch(entry);
      flushed += 1;
      processedIds.push(entry.id);
    } catch (err) {
      if (err && err.offline) {
        remaining.push(entry, ...queue.slice(i + 1));
        break;
      }
      console.error("Failed to replay offline action", err);
      remaining.push(entry, ...queue.slice(i + 1));
      break;
    }
  }
  saveQueue(remaining);
  if (typeof localStorage !== "undefined" && flushed > 0) {
    writeAccountStorageItem(LAST_SYNC_KEY, String(Date.now()));
  }
  return { flushed, remaining: remaining.length, processedIds };
}
